export type PlatformFacet = "standard" | "plus" | "premium";

type LicenseCounts = {
  totalPurchased?: number;
  totalAssigned?: number;
  free?: number;
};

export type TenantLicenses = Record<string, LicenseCounts | undefined>;

const FACETS_BY_LICENSE_KEY: Record<string, PlatformFacet> = {
  standardPlatform: "standard",
  plusPlatform: "plus",
  premiumPlatform: "premium",
};

const PLATFORM_FACET_NAMES: Record<PlatformFacet, string> = {
  standard: "Standard",
  plus: "Plus",
  premium: "Premium",
};

const LICENSE_DISPLAY_NAMES: Record<string, string> = {
  practiceManagement: "Practice Management",
  practiceManagementPro: "Practice Management Professional",
  firmManagement: "Firm Management - Workflow & Advanced Reporting",
  taxResolution: "Tax Resolution",
  irsNotices: "Notices",
  transcripts: "Transcripts & Notices",
  workflow: "Workflow",
  documentManagement: "Document Management",
  clientManagement: "Client Management",
  clientEngagement: "Client Engagement",
  timeAndBilling: "Time & Billing",
  clientManagementReportingV1: "Client Management Pro",
  timeAndBillingReportingV1: "Time & Billing Pro",
  workflowReportingV1: "Workflow Pro",
  documentManagementReportingV1: "Document Management Pro",
  engagements: "Engagements",
  smartIntake: "Smart Intake",
  closeAutomation: "Close Automation",
};

/**
 * A tenant has at most one facet — directory-service rejects more — so the
 * first match wins. A count of -1 means unlimited, hence `!== 0` not `> 0`.
 */
export function getPlatformFacet(
  licenses?: TenantLicenses | null,
): PlatformFacet | null {
  if (!licenses) return null;
  const activeKey = Object.keys(FACETS_BY_LICENSE_KEY).find((key) => {
    const counts = licenses[key];
    return (
      !!counts &&
      ((counts.totalPurchased ?? 0) !== 0 || (counts.free ?? 0) !== 0)
    );
  });
  return activeKey ? FACETS_BY_LICENSE_KEY[activeKey] : null;
}

/**
 * canopyPackagesBase is named after the tenant's facet, so pass the licenses
 * of the tenant being displayed, not the logged-in one.
 */
export function getLicenseDisplayName(
  licenseType: string,
  licenses?: TenantLicenses | null,
): string {
  if (licenseType === "canopyPackagesBase") {
    const facet = getPlatformFacet(licenses);
    return facet ? PLATFORM_FACET_NAMES[facet] : "Canopy";
  }
  const facet = FACETS_BY_LICENSE_KEY[licenseType];
  if (facet) return PLATFORM_FACET_NAMES[facet];
  return LICENSE_DISPLAY_NAMES[licenseType] ?? "";
}
